// src/components/admin/psat/PSATManagement.js
import React, { useState, useEffect } from 'react';
import { db, storage } from '../../../firebaseConfig';
import { collection, doc, setDoc, writeBatch, onSnapshot, updateDoc, query, orderBy } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import Papa from 'papaparse';
import { backdropDismiss } from '../../../utils/backdropDismiss';

const styles = {
  container: { padding: '24px', maxWidth: '1400px', margin: '0 auto', height: 'calc(100vh - 100px)' },
  grid: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px', height: '100%', alignItems: 'start' },
  sidebar: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  sidebarHeader: { padding: '16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontWeight: 700, color: '#111827', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  testList: { flex: 1, overflowY: 'auto' },
  testItem: (active) => ({
    padding: '14px 16px', cursor: 'pointer',
    background: active ? '#eff6ff' : '#fff', color: active ? '#2563eb' : '#4b5563',
    borderLeft: active ? '4px solid #2563eb' : '4px solid transparent',
    borderBottom: '1px solid #f3f4f6', fontSize: '14px', fontWeight: active ? 600 : 400
  }),
  newBtn: { fontSize: '12px', padding: '4px 8px', background: '#2563eb', color: '#fff', borderRadius: '4px', border: 'none', cursor: 'pointer' },
  content: { height: '100%', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'hidden' },
  card: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', flexShrink: 0 },
  title: { fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#6b7280', marginBottom: '16px' },
  formRow: { display: 'flex', gap: '12px', alignItems: 'flex-end' },
  input: { flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' },
  uploadBtn: { padding: '9px 20px', background: '#111827', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' },
  qListContainer: { flex: 1, background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  qHeader: { padding: '12px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 700, color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backBtn: { fontSize: '12px', padding: '6px 12px', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', cursor: 'pointer', color: '#374151' },
  qScroll: { flex: 1, overflowY: 'auto', padding: '0' },
  qRow: { display: 'flex', gap: '16px', padding: '16px', borderBottom: '1px solid #f3f4f6', alignItems: 'start' },
  qImageContainer: { width: '200px', height: '120px', border: '1px solid #eee', borderRadius: '4px', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'zoom-in' },
  qImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  qInfo: { flex: 1 },
  qMeta: { fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'flex', gap: '12px' },
  badge: { background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 },
  editBtn: { padding: '6px 12px', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#fff', borderRadius: '12px', width: '500px', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
  imgPreviewOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, cursor: 'zoom-out' },
  imgPreviewContent: { maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px', boxShadow: '0 0 20px rgba(0,0,0,0.5)' }
};

const QuestionImage = ({ path, onClick }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!path) return;
    let isMounted = true;
    getDownloadURL(ref(storage, path))
      .then((u) => { if (isMounted) setUrl(u); })
      .catch((err) => { console.warn("Image load fail:", path, err); if (isMounted) setError(true); });
    return () => { isMounted = false; };
  }, [path]);

  if (error) return <div style={{ fontSize: 10, color: '#ef4444' }}>Image not found</div>;
  if (!url) return <div style={{ fontSize: 10, color: '#9ca3af' }}>Loading...</div>;
  return <img src={url} alt="Question" style={styles.qImg} onClick={() => onClick(url)} />;
};

// Map PSAT CSV Name values to module keys
function getModuleFromName(name) {
  if (!name) return 'Unknown';
  const n = name.toLowerCase();
  if (n.includes('english_routing') || n.includes('rw_routing')) return 'English_Routing_Module';
  if (n.includes('english_module_a') || n.includes('rw_module_a')) return 'English_Module_A';
  if (n.includes('english_module_b') || n.includes('rw_module_b')) return 'English_Module_B';
  if (n.includes('math_routing')) return 'Math_Routing_Module';
  if (n.includes('math_module_a')) return 'Math_Module_A';
  if (n.includes('math_module_b')) return 'Math_Module_B';
  return 'Unknown';
}

export default function PSATManagement() {
  const [tests, setTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [viewMode, setViewMode] = useState('upload');

  const [newTestId, setNewTestId] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [isUploading, setIsUploading] = useState(false);

  const [editingQ, setEditingQ] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Scoring table upload state
  const [scoreFile, setScoreFile] = useState(null);
  const [scoreUploading, setScoreUploading] = useState(false);
  const [scoreStatus, setScoreStatus] = useState({ type: '', msg: '' });

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "psat_diagnostic_tests"), (snapshot) => {
      setTests(snapshot.docs.map(d => d.id));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!selectedTestId) { setQuestions([]); return; }
    setViewMode('list');
    const qQuery = query(
      collection(db, "psat_diagnostic_tests", selectedTestId, "questions"),
      orderBy("module"),
      orderBy("questionNumber")
    );
    const unsub = onSnapshot(qQuery, (snap) => {
      setQuestions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [selectedTestId]);

  const handleUpload = async () => {
    if (!newTestId.trim() || !csvFile) {
      setStatus({ type: 'error', msg: 'Please enter a Test ID and select a CSV file.' });
      return;
    }
    setIsUploading(true);
    setStatus({ type: 'success', msg: 'Parsing...' });

    Papa.parse(csvFile, {
      header: true, skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;
        if (!rows.length) { setIsUploading(false); return; }

        try {
          setStatus({ type: 'success', msg: `Uploading ${rows.length} questions...` });
          let batch = writeBatch(db);
          let count = 0, writes = 0;

          batch.set(doc(db, "psat_diagnostic_tests", newTestId), { testId: newTestId, createdAt: new Date() }, { merge: true });
          writes++;

          for (const row of rows) {
            if (!row.Name || !row.Answer) continue;

            const rawName = String(row.Name).trim();
            const qNum = Number(row['Question Number'] || 0);

            let uniqueId = rawName;
            if (!uniqueId.match(/Q\d+$/i) && !uniqueId.includes(String(qNum))) {
              uniqueId = `${rawName}_Q${qNum}`;
            }

            const qRef = doc(db, "psat_diagnostic_tests", newTestId, "questions", uniqueId);
            const subKey = row.Subcategory ? String(row.Subcategory).trim().replace(/–/g, "-").replace(/[^\w]+/g, "_") : "";
            const imagePath = `psat_diagnostic/${newTestId}/${uniqueId}.png`;

            const data = {
              questionId: uniqueId,
              module: getModuleFromName(rawName),
              questionNumber: qNum,
              correctAnswer: String(row.Answer).trim(),
              difficulty: row.Difficulty || 'Medium',
              category: row.Category || 'Uncategorized',
              subcategory: row.Subcategory || '',
              subcategoryKey: subKey,
              imagePath,
              explanationLink: row['Explanation Link'] || ''
            };

            batch.set(qRef, data);
            count++; writes++;
            if (writes >= 450) { await batch.commit(); batch = writeBatch(db); writes = 0; }
          }
          if (writes > 0) await batch.commit();

          setStatus({ type: 'success', msg: `✅ Success! Created ${newTestId} with ${count} questions.` });
          setNewTestId(''); setCsvFile(null);
          setSelectedTestId(newTestId);
        } catch (err) {
          console.error(err);
          setStatus({ type: 'error', msg: `Error: ${err.message}` });
        } finally { setIsUploading(false); }
      }
    });
  };

  const handleScoreUpload = () => {
    if (!selectedTestId) {
      setScoreStatus({ type: 'error', msg: 'Select a test in the sidebar first.' });
      return;
    }
    if (!scoreFile) {
      setScoreStatus({ type: 'error', msg: 'Please select the scoring CSV file.' });
      return;
    }
    setScoreUploading(true);
    setScoreStatus({ type: '', msg: 'Parsing scoring table...' });

    Papa.parse(scoreFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const rows = results.data;
          if (!rows.length) {
            setScoreStatus({ type: 'error', msg: 'CSV appears empty.' });
            setScoreUploading(false);
            return;
          }

          // Detect column names dynamically from the header row
          const keys = Object.keys(rows[0]);
          const rawKey    = keys.find(k => k.toLowerCase().includes('raw'));
          const rwAKey    = keys.find(k => k.includes('R/W') && k.includes('A'));
          const rwBKey    = keys.find(k => k.includes('R/W') && k.includes('B'));
          const mathAKey  = keys.find(k => k.toLowerCase().includes('math') && k.includes('A'));
          const mathBKey  = keys.find(k => k.toLowerCase().includes('math') && k.includes('B'));

          if (!rawKey) {
            setScoreStatus({ type: 'error', msg: 'Could not find a "Raw Score" column. Check CSV headers.' });
            setScoreUploading(false);
            return;
          }

          const parse = (v) => {
            const s = String(v ?? '').trim();
            if (!s || s.toLowerCase() === 'n/a') return null;
            const n = Number(s);
            return Number.isNaN(n) ? null : n;
          };

          const rwModuleA   = {};
          const rwModuleB   = {};
          const mathModuleA = {};
          const mathModuleB = {};

          rows.forEach(row => {
            const rawVal = String(row[rawKey] ?? '').trim();
            if (!rawVal) return;
            if (rwAKey)   { const v = parse(row[rwAKey]);   if (v !== null) rwModuleA[rawVal]   = v; }
            if (rwBKey)   { const v = parse(row[rwBKey]);   if (v !== null) rwModuleB[rawVal]   = v; }
            if (mathAKey) { const v = parse(row[mathAKey]); if (v !== null) mathModuleA[rawVal] = v; }
            if (mathBKey) { const v = parse(row[mathBKey]); if (v !== null) mathModuleB[rawVal] = v; }
          });

          const tableRef = doc(db, "psat_diagnostic_tests", selectedTestId, "scoring", "psat_score_table");
          await setDoc(tableRef, {
            rwModuleA,
            rwModuleB,
            mathModuleA,
            mathModuleB,
            uploadedAt: new Date(),
          });

          const rwCount   = Object.keys(rwModuleA).length + Object.keys(rwModuleB).length;
          const mathCount = Object.keys(mathModuleA).length + Object.keys(mathModuleB).length;
          setScoreStatus({
            type: 'success',
            msg: `✅ Scoring table uploaded! ${rwCount} R/W entries · ${mathCount} Math entries stored.`,
          });
          setScoreFile(null);
        } catch (err) {
          console.error(err);
          setScoreStatus({ type: 'error', msg: `Error: ${err.message}` });
        } finally {
          setScoreUploading(false);
        }
      },
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingQ) return;
    try {
      const qRef = doc(db, "psat_diagnostic_tests", selectedTestId, "questions", editingQ.id);
      await updateDoc(qRef, {
        correctAnswer: editingQ.correctAnswer,
        difficulty: editingQ.difficulty,
        subcategory: editingQ.subcategory,
        explanationLink: editingQ.explanationLink
      });
      setEditingQ(null);
    } catch (err) {
      alert("Error saving: " + err.message);
    }
  };

  const handleBack = () => { setSelectedTestId(null); setViewMode('upload'); };

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <span>PSAT Tests</span>
            <button style={styles.newBtn} onClick={handleBack}>+ New</button>
          </div>
          <div style={styles.testList}>
            {tests.map(id => (
              <div key={id} style={styles.testItem(selectedTestId === id)} onClick={() => setSelectedTestId(id)}>
                {id}
              </div>
            ))}
          </div>
        </div>

        <div style={styles.content}>
          {viewMode === 'upload' && (
            <div style={styles.card}>
              <h2 style={styles.title}>Create New PSAT Diagnostic Test</h2>
              <p style={styles.subtitle}>
                Upload a CSV to generate PSAT questions. Expected columns: Name, Question Number, Answer, Difficulty, Category, Subcategory, Explanation Link.
                <br />
                Images must already be uploaded to: <code>psat_diagnostic/&#123;testId&#125;/&#123;questionId&#125;.png</code>
              </p>
              <div style={styles.formRow}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 600 }}>Test ID (e.g. psat_diagnostic_test1)</div>
                  <input style={styles.input} value={newTestId} onChange={e => setNewTestId(e.target.value)} placeholder="Unique Test ID" />
                </div>
                <div style={{ flex: 2 }}>
                  <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 600 }}>CSV File</div>
                  <input type="file" accept=".csv" onChange={e => setCsvFile(e.target.files[0])} style={styles.input} />
                </div>
                <button style={styles.uploadBtn} onClick={handleUpload} disabled={isUploading}>
                  {isUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
              {status.msg && (
                <div style={{ marginTop: 15, fontSize: 14, color: status.type === 'error' ? 'red' : 'green' }}>
                  {status.msg}
                </div>
              )}
            </div>
          )}

          {viewMode === 'list' && selectedTestId && (
            <div style={styles.card}>
              <div style={styles.title}>Upload Scoring Table (Raw → Scale)</div>
              <p style={styles.subtitle}>
                Upload the PSAT scoring CSV with columns: <strong>Raw Score</strong>, <strong>R/W Module A</strong>, <strong>R/W Module B</strong>, <strong>Math Module A</strong>, <strong>Math Module B</strong>.
                Cells containing "N/A" are automatically skipped.
              </p>
              <div style={styles.formRow}>
                <input
                  type="file"
                  accept=".csv"
                  onChange={e => { setScoreFile(e.target.files[0]); setScoreStatus({ type: '', msg: '' }); }}
                  style={styles.input}
                />
                <button
                  style={{ ...styles.uploadBtn, opacity: scoreUploading ? 0.6 : 1 }}
                  onClick={handleScoreUpload}
                  disabled={scoreUploading}
                >
                  {scoreUploading ? 'Uploading...' : 'Upload Scale'}
                </button>
              </div>
              {scoreStatus.msg && (
                <div style={{ marginTop: 12, fontSize: 13, color: scoreStatus.type === 'error' ? '#dc2626' : '#16a34a' }}>
                  {scoreStatus.msg}
                </div>
              )}
            </div>
          )}

          {viewMode === 'list' && selectedTestId && (
            <div style={styles.qListContainer}>
              <div style={styles.qHeader}>
                <span>Questions for: {selectedTestId} ({questions.length})</span>
                <button style={styles.backBtn} onClick={handleBack}>← Back to Dashboard</button>
              </div>
              <div style={styles.qScroll}>
                {questions.length === 0 && <div style={{ padding: 20, color: '#999' }}>No questions found.</div>}
                {questions.map(q => (
                  <div key={q.id} style={styles.qRow}>
                    <div style={styles.qImageContainer}>
                      <QuestionImage path={q.imagePath} onClick={setPreviewImage} />
                    </div>
                    <div style={styles.qInfo}>
                      <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>{q.module} - #{q.questionNumber}</div>
                      <div style={styles.qMeta}>
                        <span style={styles.badge}>{q.category}</span>
                        <span style={styles.badge}>{q.difficulty}</span>
                        <span>Ans: <strong>{q.correctAnswer}</strong></span>
                      </div>
                      <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                        {q.subcategory}
                        {q.explanationLink && <a href={q.explanationLink} target="_blank" rel="noreferrer" style={{ marginLeft: 10, color: '#2563eb' }}>View Explanation</a>}
                      </div>
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>{q.imagePath}</div>
                    </div>
                    <button style={styles.editBtn} onClick={() => setEditingQ(q)}>Edit</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {editingQ && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3>Edit Question {editingQ.questionNumber}</h3>
            <form onSubmit={handleSaveEdit}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Correct Answer</label>
                <input style={styles.input} value={editingQ.correctAnswer} onChange={e => setEditingQ({ ...editingQ, correctAnswer: e.target.value })} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Difficulty</label>
                <select style={styles.input} value={editingQ.difficulty} onChange={e => setEditingQ({ ...editingQ, difficulty: e.target.value })}>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Subcategory</label>
                <input style={styles.input} value={editingQ.subcategory} onChange={e => setEditingQ({ ...editingQ, subcategory: e.target.value })} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Explanation Link</label>
                <input style={styles.input} value={editingQ.explanationLink} onChange={e => setEditingQ({ ...editingQ, explanationLink: e.target.value })} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Image Path (Read-only)</label>
                <input style={{ ...styles.input, background: '#f3f4f6' }} value={editingQ.imagePath} disabled />
              </div>
              <div style={styles.modalActions}>
                <button type="button" onClick={() => setEditingQ(null)} style={{ ...styles.newBtn, background: '#fff', color: '#333', border: '1px solid #ccc' }}>Cancel</button>
                <button type="submit" style={{ ...styles.newBtn, padding: '8px 16px' }}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {previewImage && (
        <div style={styles.imgPreviewOverlay} {...backdropDismiss(() => setPreviewImage(null))}>
          <img src={previewImage} alt="Zoomed Question" style={styles.imgPreviewContent} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
